import { useT } from '@transifex/react';
import type { JSX, ChangeEvent } from 'react';
import { Fragment, useRef, forwardRef } from 'react';
import { useFormContext } from 'react-hook-form';

import { cssClasses } from 'pkg/css/utils';

import MaterialSymbol from 'components/material-symbols';

import { InfoText } from 'components/form/inputs/info_text';
import { Label } from 'components/form/inputs/label';

import Toggle from 'design/toggle';

import * as css from './styles.css';

type ControlColor = 'gray' | 'red' | 'orange' | 'yellow' | 'green' | 'blue';

const getControlColor = (color: ControlColor): string => {
	switch (color) {
		case 'gray':
			return css.gray;
		case 'red':
			return css.red;
		case 'orange':
			return css.orange;
		case 'yellow':
			return css.yellow;
		case 'green':
			return css.green;
		default:
			return css.blue;
	}
};

export type ControlOnChange = (
	value: string | number | readonly string[],
	target?: HTMLInputElement,
	event?: ChangeEvent<HTMLInputElement>
) => void;

interface Props {
	type: 'toggle' | 'radio' | 'checkbox';
	label?: string;
	description?: string;
	optional?: boolean;
	required?: boolean;
	disabled?: boolean;
	// Like disabled (unfocusable, ignores clicks and keyboard) but keeps the
	// default styling — for controls locked in a state that should stay
	// readable, e.g. an already-connected row in a pick list.
	inert?: boolean;

	position?: 'before' | 'after';
	checked?: boolean;
	defaultChecked?: boolean;
	color?: ControlColor;
	standalone?: boolean;
	rounded?: boolean;
	large?: boolean;
	name?: string;
	value?: string | number | readonly string[];
	className?: string;
	// Accessible name for the input; standalone controls render no label
	// element, so without it screen readers announce a bare checkbox/radio.
	'aria-label'?: string;
	testid?: string;
	noRequiredText?: boolean;
	hasError?: boolean;

	children?: JSX.Element;
	lineThrough?: boolean;
	onChange?: ControlOnChange;
	// Callback for custom validation
	validate?: (
		value: string | number
	) => string | string[] | boolean | undefined;
}

/**
 * Control component
 *
 * A flexible form control that supports toggle, radio, and checkbox input types.
 * Integrates with react-hook-form for validation
 */

export const Control = forwardRef<HTMLInputElement, Props>(
	(
		{
			type,
			label,
			description,
			optional,
			required,
			disabled,
			inert,

			position = 'before',
			checked,
			defaultChecked,
			color = 'blue',

			standalone,
			rounded,
			large,
			lineThrough = false,

			name,
			value,
			hasError,
			onChange,
			children,
			className,
			testid,
			validate,

			noRequiredText,

			...rest
		},
		forwardedRef
	) => {
		const t = useT();
		const methods = useFormContext();

		const validationRules =
			!onChange &&
			name &&
			methods &&
			methods.register(name, {
				required: required,
				validate,
			});

		const inputRef = useRef<HTMLInputElement>(null);
		const controlRef = useRef<HTMLDivElement>(null);

		const colorCssClass = getControlColor(color);

		const inputRefCallback = (e: HTMLInputElement) => {
			if (validationRules) {
				validationRules.ref(e);
			}
			inputRef.current = e;
		};

		const controlRefCallback = (e: HTMLDivElement) => {
			if (forwardedRef) {
				if (typeof forwardedRef === 'function') {
					forwardedRef(e as any);
				} else {
					forwardedRef.current = e as any;
				}
			}
			controlRef.current = e;
		};

		const isInteractive = !disabled && !inert;

		const handleClick = () => {
			if (isInteractive) {
				inputRef.current?.click();
			}
		};

		const handleKeyDown = (e: React.KeyboardEvent) => {
			if (e.key === 'Enter' || e.key === ' ') {
				e.preventDefault();
				handleClick();
			}
		};

		let control;

		switch (type) {
			case 'toggle':
				control = <Toggle active={checked} data-testid={testid} />;
				break;
			case 'radio':
				control = (
					<div
						className={cssClasses(
							css.control,
							colorCssClass,
							css.rounded,
							large && css.large,
							hasError && css.hasError
						)}
						data-testid={testid}>
						<svg viewBox="0 0 14 14">
							<circle cx="7" cy="7" r="4" fill="currentColor" />
						</svg>
					</div>
				);
				break;
			case 'checkbox':
				control = (
					<div
						className={cssClasses(
							css.control,
							colorCssClass,
							large && css.large,
							rounded && css.rounded,
							hasError && css.hasError
						)}
						data-testid={testid}>
						<MaterialSymbol variant="check" scale={1.5} />
					</div>
				);
				break;
		}

		const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
			if (onChange) {
				onChange(value, inputRef.current, e);
			} else if (validationRules) {
				validationRules.onChange(e);
			}
		};

		const controlValue = (
			<input
				{...rest}
				ref={inputRefCallback}
				disabled={!isInteractive}
				type={type === 'radio' ? 'radio' : 'checkbox'}
				name={name}
				value={value}
				defaultChecked={defaultChecked}
				checked={checked}
				tabIndex={-1}
				onChange={handleChange}
				onClick={(e) => {
					e.stopPropagation();
				}}
			/>
		);

		if (standalone) {
			const standaloneControl = (
				<Fragment>
					{controlValue}
					{control}
				</Fragment>
			);

			return (
				<div
					className={cssClasses(
						css.controlGroup,
						css.standalone,
						className && className,
						disabled && css.disabled,
						inert && css.inert
					)}
					aria-disabled={!isInteractive || undefined}
					onClick={handleClick}>
					{standaloneControl}
				</div>
			);
		}

		return (
			<div
				className={cssClasses(
					css.controlGroup,
					colorCssClass,
					className && className,
					disabled && css.disabled,
					inert && css.inert,
					position === 'after' && css.positionAfter
				)}
				ref={controlRefCallback}
				onKeyDown={handleKeyDown}
				tabIndex={isInteractive ? 0 : -1}
				aria-disabled={!isInteractive || undefined}
				onClick={handleClick}>
				{controlValue}
				{position === 'before' && control}
				<div>
					<Label lineThrough={lineThrough}>
						{children || label}
						{optional && <span>{t('Optional')}</span>}
						{required && !noRequiredText && <span>{t('Required')}</span>}
					</Label>
					{description && <InfoText>{description}</InfoText>}
				</div>
				{position === 'after' && control}
			</div>
		);
	}
);
